import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CancelIcon from '@mui/icons-material/Cancel';
import { ExportJob } from '../../api/invoiceApi';
import { AppIconButton } from '../ui';

interface ExportProgressModalProps {
  open: boolean;
  onClose: () => void;
  currentJob: ExportJob | null;
  recentJobs: ExportJob[];
  isExporting: boolean;
  onCancel: () => void;
  onDownload: () => void;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'COMPLETED':
      return <CheckCircleIcon color="success" />;
    case 'FAILED':
      return <ErrorIcon color="error" />;
    case 'CANCELLED':
      return <CancelIcon color="disabled" />;
    case 'PROCESSING':
      return <SyncIcon color="primary" sx={{ animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />;
    case 'PENDING':
    default:
      return <HourglassEmptyIcon color="action" />;
  }
};

const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'CANCELLED':
      return 'default';
    case 'PROCESSING':
      return 'info';
    case 'PENDING':
    default:
      return 'warning';
  }
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

export const ExportProgressModal: React.FC<ExportProgressModalProps> = ({
  open,
  onClose,
  currentJob,
  recentJobs,
  isExporting,
  onCancel,
  onDownload
}) => {
  const progress = currentJob?.progressPercent || 0;
  const totalRecords = currentJob?.totalRecords || 0;
  const processedRecords = currentJob?.processedRecords || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Export Progress</Typography>
          <AppIconButton
            icon={<CloseIcon />}
            tooltip="Close"
            variant="ghost"
            size="small"
            onClick={onClose}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {currentJob && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                Current Export
              </Typography>
              <Chip
                label={currentJob.status}
                size="small"
                color={getStatusColor(currentJob.status)}
              />
            </Box>

            {isExporting && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {currentJob.status === 'PENDING' ? 'Preparing export...' : 'Processing records...'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {totalRecords > 0 ? `${processedRecords} / ${totalRecords}` : ''}
                  </Typography>
                </Box>
                <LinearProgress
                  variant={progress > 0 ? 'determinate' : 'indeterminate'}
                  value={progress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                {progress > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {progress}% complete
                  </Typography>
                )}
              </Box>
            )}

            {currentJob.status === 'COMPLETED' && (
              <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: 'success.dark', mb: 1 }}>
                  Export completed successfully!
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {currentJob.totalRecords} records exported
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  File: {currentJob.fileName}
                </Typography>
              </Box>
            )}

            {currentJob.status === 'FAILED' && (
              <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: 'error.dark' }}>
                  Export failed: {currentJob.errorMessage || 'Unknown error'}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Started: {formatDate(currentJob.startedAt)}
              </Typography>
              {currentJob.completedAt && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Completed: {formatDate(currentJob.completedAt)}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {recentJobs.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Recent Exports
            </Typography>
            <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
              {recentJobs.slice(0, 5).map((job) => (
                <ListItem key={job.id} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {getStatusIcon(job.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        {formatDate(job.createdAt)}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {job.totalRecords || 0} records
                      </Typography>
                    }
                  />
                  {job.status === 'COMPLETED' && job.fileUrl && (
                    <AppIconButton
                      icon={<FileDownloadIcon fontSize="small" />}
                      tooltip="Download export"
                      variant="ghost"
                      size="small"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `/api${job.fileUrl}`;
                        link.download = job.fileName || 'export.xlsx';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    />
                  )}
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {isExporting ? (
          <Button
            onClick={onCancel}
            color="error"
            variant="outlined"
            startIcon={<CancelIcon />}
          >
            Cancel Export
          </Button>
        ) : currentJob?.status === 'COMPLETED' && currentJob.fileUrl ? (
          <Button
            onClick={onDownload}
            color="primary"
            variant="contained"
            startIcon={<FileDownloadIcon />}
          >
            Download
          </Button>
        ) : null}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportProgressModal;
